/**
 * Admin API Client for DealOS Auditing API
 * Centralized client for accessing audit data from the backend API
 */

import { acquireBackendApiToken } from '../../utils/msalTokenService';
import { handleAuthError } from '../../utils/authErrorHandler';
import type {
  ConversationFilters,
  ConversationsResponse,
  ConversationDetail,
  TurnDetailResponse,
  ActionDetailResponse,
  LLMRequest,
  Turn,
  Action,
  TurnOutput
} from './types';

const API_BASE_URL = import.meta.env.VITE_AUDIT_API_BASE_URL;

class AdminAPIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get authenticated headers with bearer token
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    console.log('🔐 [AdminAPIClient] Getting authenticated headers...');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    try {
      const token: string = await acquireBackendApiToken();
      console.log('✅ [AdminAPIClient] Acquired backend API token, length:', token?.length);
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('❌ [AdminAPIClient] Failed to acquire backend API token:', error);
      handleAuthError();
      throw new Error('Authentication failed — redirecting to login');
    }

    return headers;
  }

  /**
   * Make HTTP request with error handling
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get authenticated headers
    const authHeaders = await this.getAuthHeaders();

    const defaultOptions = {
      headers: {
        ...authHeaders,
        ...(options.headers || {}),
      },
      ...options,
    };

    const response = await fetch(url, defaultOptions);

    if (response.status === 401 || response.status === 403) {
      handleAuthError();
      throw new Error('Session expired — redirecting to login');
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  }

  /**
   * Build query string from parameters
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const filtered = Object.entries(params)
      .filter(([_key, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

    return filtered.length > 0 ? `?${filtered.join('&')}` : '';
  }

  /**
   * Map the camelCase UI filters onto the API's snake_case query params.
   *
   * `search` is sent to the server rather than applied to the fetched page, so
   * it spans the whole result set and the pagination totals stay truthful.
   */
  private buildConversationParams(filters: ConversationFilters): Record<string, unknown> {
    // buildQueryString drops null/undefined/'' entries, so optional filters can
    // be listed unconditionally rather than behind one branch each.
    const params: Record<string, unknown> = {
      page: filters.page ?? 1,
      page_size: filters.page_size ?? 50,
      username: filters.username,
      search: filters.search,
      environment: filters.environment,
      channel: filters.channel,
      from_date: filters.dateFrom,
      to_date: filters.dateTo,
      has_errors: filters.hasErrors,
      has_positive_feedback: filters.hasPositiveFeedback,
      has_negative_feedback: filters.hasNegativeFeedback,
      hit_limits: filters.hitLimits,
      has_turns: filters.hasTurns,
    };

    // A half-typed value like "-" parses to NaN. An absent entry is dropped by
    // buildQueryString, but a NaN would be sent to the API literally.
    const minTime = parseInt(String(filters.minTimeMs ?? ""));
    if (Number.isFinite(minTime)) params.min_time_ms = minTime;
    const maxTime = parseInt(String(filters.maxTimeMs ?? ""));
    if (Number.isFinite(maxTime)) params.max_time_ms = maxTime;

    return params;
  }

  /**
   * Get conversations with filtering and pagination
   */
  async getConversations(filters: ConversationFilters = {}): Promise<ConversationsResponse> {
    const queryString = this.buildQueryString(this.buildConversationParams(filters));
    const response = await this.request<ConversationsResponse>(
      `/api/v1/conversations${queryString}`
    );

    return {
      conversations: response.conversations || [],
      pagination: response.pagination
    };
  }

  /**
   * Get detailed conversation data
   */
  async getConversationDetail(conversationId: string): Promise<ConversationDetail> {
    return await this.request<ConversationDetail>(`/api/v1/conversations/${conversationId}`);
  }

  /**
   * Get turns for a conversation (extracted from conversation detail)
   */
  async getTurns(conversationId: string): Promise<Turn[]> {
    const detail = await this.getConversationDetail(conversationId);
    return detail.turns || [];
  }

  /**
   * Get specific turn data (find from conversation detail)
   */
  async getTurn(conversationId: string, turnId: string): Promise<TurnDetailResponse> {
    const detail = await this.getConversationDetail(conversationId);
    const turn = detail.turns.find(t => t.turn_id === turnId);

    if (!turn) {
      throw new Error(`Turn ${turnId} not found in conversation ${conversationId}`);
    }

    // Enhance turn with related data
    const turnOutputs = detail.turn_outputs.filter(output => output.turn_id === turnId);
    const actions = detail.actions.filter(action => action.turn_id === turnId);

    return {
      turn,
      outputs: turnOutputs,
      actions: actions.map(action => ({
        ...action,
        llm_request_count: detail.llm_requests.filter(req => req.action_id === action.action_id).length
      }))
    };
  }

  /**
   * Get turn outputs for a specific turn
   */
  getTurnOutputs(_turnId: string): Promise<TurnOutput[]> {
    // We need conversation ID to get the detail, but this method assumes we don't have it
    // This would require a separate API endpoint or we store context
    throw new Error("getTurnOutputs requires conversation context - use getTurn instead");
  }

  /**
   * Get actions for a specific turn
   */
  getActions(_turnId: string): Promise<Action[]> {
    // Same issue as getTurnOutputs - need conversation context
    throw new Error("getActions requires conversation context - use getTurn instead");
  }

  /**
   * Get specific action data
   */
  async getAction(conversationId: string, actionId: string): Promise<ActionDetailResponse> {
    const detail = await this.getConversationDetail(conversationId);
    const action = detail.actions.find(a => a.action_id === actionId);

    if (!action) {
      throw new Error(`Action ${actionId} not found in conversation ${conversationId}`);
    }

    const llmRequests = detail.llm_requests.filter(req => req.action_id === actionId);

    return {
      action,
      llm_requests: llmRequests
    };
  }

  /**
   * Get LLM requests for an action
   */
  getLLMRequests(_actionId: string): Promise<LLMRequest[]> {
    // This would require conversation context or a separate endpoint
    throw new Error("getLLMRequests requires conversation context - use getAction instead");
  }

  /**
   * Get specific LLM request
   */
  async getLLMRequest(conversationId: string, llmRequestId: string): Promise<LLMRequest> {
    const detail = await this.getConversationDetail(conversationId);
    const llmRequest = detail.llm_requests.find(req => req.llm_request_id === llmRequestId);

    if (!llmRequest) {
      throw new Error(`LLM Request ${llmRequestId} not found in conversation ${conversationId}`);
    }

    return llmRequest;
  }

  /**
   * Get analytics summary
   */
  async getAnalyticsSummary(dateRange: { from_date?: string; to_date?: string } = {}): Promise<unknown> {
    const params: Record<string, string> = {};
    if (dateRange.from_date) params.from_date = dateRange.from_date;
    if (dateRange.to_date) params.to_date = dateRange.to_date;

    const queryString = this.buildQueryString(params);
    return await this.request<unknown>(`/api/v1/analytics/summary${queryString}`);
  }

  /**
   * Get user activity analytics
   */
  async getUserActivity(options: { limit?: number; from_date?: string; to_date?: string } = {}): Promise<unknown> {
    const params: Record<string, unknown> = {
      limit: options.limit || 20,
      ...(options.from_date && { from_date: options.from_date }),
      ...(options.to_date && { to_date: options.to_date })
    };

    const queryString = this.buildQueryString(params);
    return await this.request<unknown>(`/api/v1/analytics/user-activity${queryString}`);
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<unknown> {
    return await this.request<unknown>('/health');
  }
}

// Export singleton instance
export const adminAPI = new AdminAPIClient();
export default adminAPI;
